import React, { useState, useEffect } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import ReactQuill from 'react-quill';
import Select from 'react-select';
import 'react-quill/dist/quill.snow.css';
import './SignatureModal.css';

const modules = {
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }],
        ['link', 'image'],
        ['clean']
    ]
};

const formats = ['bold', 'italic', 'underline', 'strike', 'color', 'link', 'image'];

const SignatureModal = ({ isOpen, toggle, signature, firstName: initialFirstName, lastName: initialLastName, salesAccounts, selectedSalesAccounts: initialSelected, onSave, saving }) => {
    const [content, setContent] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [selectedAccounts, setSelectedAccounts] = useState([]);

    const accountOptions = (salesAccounts || []).flatMap(acc => {
        const emailIntegrations = (acc.integrations || []).filter(i => {
            const toolName = (i.toolId?.name || '').toLowerCase();
            const emailAddr = i.tokens?.email || i.accountIdentifier || '';
            return toolName === 'gmail' || toolName === 'outlook' || (emailAddr && emailAddr.includes('@'));
        });
        if (emailIntegrations.length === 0) return [];
        return emailIntegrations.map(i => ({ value: `${acc._id}__${i._id}`, label: i.tokens?.email || i.accountIdentifier || i.name, accountId: acc._id, accountName: acc.name }));
    });

    useEffect(() => {
        if (isOpen) {
            setContent(signature || '');
            setFirstName(initialFirstName || '');
            setLastName(initialLastName || '');
            const preSelected = (initialSelected || []).map(id => accountOptions.find(opt => opt.value === id)).filter(Boolean);
            setSelectedAccounts(preSelected);
        }
    }, [isOpen, signature, initialFirstName, initialLastName]);

    const handleSave = () => onSave({ content, firstName, lastName, selectedAccounts });

    return (
        <Modal isOpen={isOpen} toggle={toggle} className="main-modal signature-modal">
            <ModalBody className="sig-modal-body">
                {/* Header */}
                <div className="sig-header">
                    <div className="sig-header-left">
                        <div className="sig-header-icon">
                            <svg width="20" height="16" viewBox="0 0 16 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15.3665 4.01739L11.3492 0L9.19528 2.1539L5.3791 4.42421L3.40006 11.407L3.08141 11.7257C2.8405 11.9666 2.5202 12.0993 2.1795 12.0993C1.83879 12.0993 1.5185 11.9666 1.27755 11.7257C0.794108 11.2422 0.77667 10.4681 1.23788 9.96334L1.84657 9.29711C2.63344 8.43593 2.60373 7.11519 1.77883 6.29032L0.636901 5.14839L0 5.78532L1.14193 6.92725C1.62537 7.41069 1.64281 8.18481 1.1816 8.68957L0.572914 9.35579C-0.213961 10.217 -0.184248 11.5377 0.640653 12.3626C1.05168 12.7736 1.59818 13 2.1795 13C2.76081 13 3.30729 12.7736 3.71831 12.3626L4.03561 12.0453L11.092 10.011L13.2178 6.16615L15.3665 4.01739ZM11.3492 1.27386L14.0927 4.01739L12.8544 5.25572L10.1109 2.51216L11.3492 1.27386ZM10.4736 9.26788L5.29896 10.7876L7.91788 8.17179L8.83636 8.16798L8.81868 6.6146L7.27179 6.63225L7.27564 7.54026L4.6756 10.1372L6.13056 5.02528L9.40315 3.07835L12.2962 5.97143L10.4736 9.26788Z" fill="currentColor" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="sig-header-title">Email Signature</h3>
                            <p className="sig-header-desc">Automatically appended to emails sent from this sales account</p>
                        </div>
                    </div>
                    <button className="sig-close-btn" onClick={toggle}><i className="fa fa-times" /></button>
                </div>

                {/* Body */}
                <div className="sig-content">
                    {/* Accounts */}
                    <div className="sig-section">
                        <label className="sig-label">Apply to accounts</label>
                        <Select
                            isMulti
                            options={accountOptions}
                            value={selectedAccounts}
                            onChange={setSelectedAccounts}
                            placeholder="Select email accounts..."
                            classNamePrefix="sig-select"
                            className="sig-select-container"
                        />
                    </div>

                    {/* Name fields */}
                    <div className="sig-section sig-name-row">
                        <div className="sig-field">
                            <label className="sig-label">First Name</label>
                            <input type="text" className="sig-input" placeholder="First name" value={firstName} onChange={e => setFirstName(e['target']['value'])} />
                        </div>
                        <div className="sig-field">
                            <label className="sig-label">Last Name</label>
                            <input type="text" className="sig-input" placeholder="Last name" value={lastName} onChange={e => setLastName(e['target']['value'])} />
                        </div>
                    </div>

                    {/* Editor + Preview */}
                    <div className="sig-editor-row">
                        <div className="sig-editor-col">
                            <label className="sig-label">Signature</label>
                            <div className="sig-editor-wrap">
                                <ReactQuill theme="snow" value={content} onChange={setContent} modules={modules} formats={formats} placeholder="Enter your signature..." />
                            </div>
                        </div>
                        <div className="sig-preview-col">
                            <label className="sig-label">Preview</label>
                            <div className="sig-preview-wrap">
                                <div className="sig-preview-email-chrome">
                                    <div className="sig-preview-chrome-dot" />
                                    <div className="sig-preview-chrome-dot" />
                                    <div className="sig-preview-chrome-dot" />
                                </div>
                                <div className="sig-preview-email-body">
                                    <p className="sig-preview-placeholder-text">Hi [First Name],</p>
                                    <p className="sig-preview-placeholder-text">Your email body will appear here...</p>
                                    <div className="sig-preview-divider" />
                                    {content
                                        ? <div className="sig-preview-content" dangerouslySetInnerHTML={{ __html: content }} />
                                        : <span className="sig-preview-empty">Your signature will appear here...</span>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="sig-footer">
                    <button className="btn-style-border" onClick={toggle}>Cancel</button>
                    <button className="btn-style-new" onClick={handleSave} disabled={saving}>
                        {saving ? <><i className="fa fa-spinner fa-spin icon" /> Saving...</> : <><i className="fa fa-check icon" /> Save Signature</>}
                    </button>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default SignatureModal;
